import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Box, Typography, useTheme, IconButton, Tooltip } from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import { setSelectedInboxTab } from "../../../../redux/Slice/Inbox/inboxSlice";
import { selectUserDetailsData } from "../../../../redux/selectors";

// Same small dark-bubble tooltip convention used across the Dashboard cards (see
// ContactRow.jsx's ASSIGNEE_TOOLTIP_SX / ContactsPanel.jsx's TAB_TOOLTIP_SX) — mirrors
// ASSIGNEE_TOOLTIP_SX's compact sizing exactly, duplicated locally rather than shared
// since none of these small per-file tooltip style objects are extracted yet.
const UNASSIGNED_TOOLTIP_SX = {
  tooltip: {
    sx: {
      backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
      color: (theme) => (theme.palette.mode === "dark" ? "#1A1A1A" : "#F5F5F5"),
      fontSize: "0.7rem",
      fontWeight: 500,
      borderRadius: "6px",
      maxWidth: 150,
      textAlign: "center",
      px: 1,
      py: 0.5,
      boxShadow: (theme) =>
        theme.palette.mode === "dark" ? "0 6px 16px rgba(0,0,0,0.35)" : "0 4px 12px rgba(0,0,0,0.15)",
    },
  },
  arrow: {
    sx: {
      color: (theme) => (theme.palette.mode === "dark" ? "#E2E2E4" : "#1A1A1A"),
    },
  },
};

function LifecycleRow({ stage }) {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountid } = useParams();
  const userDetails = useSelector(selectUserDetailsData);
  // stage.icon is a plain emoji character (see LifecycleStageModel/LifecycleSettings.jsx's
  // emoji-mart picker), not a component — fall back to a plain outline when unset.
  const hasTrend = stage.trend !== undefined && stage.trend !== null;
  const positive = stage.trend >= 0;
  const trendColor = positive ? "#22c55e" : "#ef4444";

  // Opens this stage's contact list in the Inbox — same lifecycle:<stageId> tab format
  // and setSelectedInboxTab action the Inbox sidebar's own Lifecycle rail already uses
  // (InboxSettings.jsx), just triggered from this Dashboard card instead (Rahim,
  // 2026-09-01: "connect the enlarge icon to its required chat").
  const handleExpand = () => {
    const effectiveUserid = userDetails?._id;
    if (!accountid || !effectiveUserid || !stage.stageId) return;
    dispatch(setSelectedInboxTab(`lifecycle:${stage.stageId}`));
    navigate(`/home/${accountid}/inbox/${effectiveUserid}`);
  };
  return (
      <Box
        sx={{
          backgroundColor: theme.palette.mode === "light" ? theme.palette.background.default : theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "16px",
          boxShadow: theme.palette.mode === "dark" ? "0 2px 8px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.06)",
          p: 1.75,
          mb: 0.9,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {stage.icon ? (
          <Box sx={{ fontSize: "1.625rem", lineHeight: 1, width: 28, textAlign: "center", flexShrink: 0 }}>
            {stage.icon}
          </Box>
        ) : (
          <CircleOutlinedIcon sx={{ fontSize: 28, color: stage.color, flexShrink: 0 }} />
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 500 }}>{stage.label}</Typography>
          <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
            <Typography sx={{ fontWeight: 600, fontSize: "1.75rem", lineHeight: 1.3 }}>{stage.count}</Typography>
            {hasTrend && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, color: trendColor, fontSize: "0.8125rem", fontWeight: 600 }}>
                {positive ? <TrendingUpIcon sx={{ fontSize: 14 }} /> : <TrendingDownIcon sx={{ fontSize: 14 }} />}
                {Math.abs(stage.trend)}%
              </Box>
            )}
          </Box>
        </Box>

        <Tooltip title={`${stage.unassigned} unassigned`} placement="bottom" arrow componentsProps={UNASSIGNED_TOOLTIP_SX}>
          <IconButton size="small" sx={{ flexShrink: 0 }}>
            <PersonOffOutlinedIcon
              sx={{ fontSize: 19, color: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)") }}
            />
          </IconButton>
        </Tooltip>

        <Tooltip title="Expand" placement="bottom" arrow componentsProps={UNASSIGNED_TOOLTIP_SX}>
          <IconButton size="small" onClick={handleExpand} sx={{ flexShrink: 0 }}>
            <OpenInFullIcon
              sx={{ fontSize: 17, color: (t) => (t.palette.mode === "dark" ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)") }}
            />
          </IconButton>
        </Tooltip>
      </Box>
  );
}

export default LifecycleRow;
